import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCustomerContacts } from "@/hooks/use-customer-contacts";
import { useLogCustomerActivity, type LogActivityInput } from "@/hooks/use-customer-activities";

const NO_CONTACT = "none";

const COPY: Record<LogActivityInput["type"], { title: string; bodyLabel: string; placeholder: string }> = {
  call: { title: "Logg samtale", bodyLabel: "Hva ble sagt?", placeholder: "Kort oppsummering av samtalen" },
  note: { title: "Logg notat", bodyLabel: "Notat", placeholder: "Skriv notatet her" },
  meeting: { title: "Logg møte", bodyLabel: "Referat", placeholder: "Kort oppsummering av møtet" },
};

interface LogActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  type: LogActivityInput["type"];
  defaultContactId?: string | null;
}

/** Manuell logging av samtale, notat eller møte i kontaktloggen. */
export default function LogActivityDialog({ open, onOpenChange, customerId, type, defaultContactId }: LogActivityDialogProps) {
  const { data: contacts = [] } = useCustomerContacts(customerId);
  const log = useLogCustomerActivity();
  const [contactId, setContactId] = useState<string>(NO_CONTACT);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const selectable = contacts.filter((c) => c.is_active && !c.anonymized_at);
  const copy = COPY[type];

  useEffect(() => {
    if (!open) return;
    const preferred = selectable.find((c) => c.id === defaultContactId) ?? selectable.find((c) => c.is_primary);
    setContactId(preferred?.id ?? NO_CONTACT);
    setSubject("");
    setBody("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, defaultContactId]);

  const canSave = body.trim().length > 0 && !log.isPending;

  const save = async () => {
    const contact = selectable.find((c) => c.id === contactId);
    await log.mutateAsync({
      customer_id: customerId,
      type,
      contact_id: contact?.id ?? null,
      contact_name: contact?.full_name ?? null,
      subject,
      body,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) save().catch(() => undefined);
          }}
        >
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="log-activity-contact">Kontaktperson</Label>
              <Select value={contactId} onValueChange={setContactId}>
                <SelectTrigger id="log-activity-contact" aria-label="Velg kontaktperson">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CONTACT}>Ingen bestemt person</SelectItem>
                  {selectable.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {type !== "note" && (
              <div className="space-y-1.5">
                <Label htmlFor="log-activity-subject">Emne (valgfritt)</Label>
                <Input id="log-activity-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="log-activity-body">{copy.bodyLabel}</Label>
              <Textarea
                id="log-activity-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                placeholder={copy.placeholder}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" disabled={!canSave} aria-label="Lagre i kontaktloggen">
              Lagre
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
